import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  PhoneOff,
  Loader2,
  Clock,
  Volume2,
  UserCircle,
  Shield,
  Zap,
  Flame,
  Snowflake,
  Smile,
  Radio,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSONA_ICONS = {
  budget_conscious: Snowflake,
  busy_decision_maker: Zap,
  technical_buyer: Shield,
  skeptical_buyer: Flame,
  friendly_noncommittal: Smile
};

export default function VoiceRoleplay() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callState, setCallState] = useState('connecting'); // connecting | active | ai_speaking | listening | processing | ending
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [fallbackToChat, setFallbackToChat] = useState(false);
  const [chatInput, setChatInput] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const recTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch session
  useEffect(() => {
    fetchSession();
    return () => cleanup();
  }, [sessionId]);

  // Call timer
  useEffect(() => {
    if (session?.status === 'active' && callState !== 'connecting') {
      const startTime = new Date(session.created_at).getTime();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [session, callState]);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setTranscript(data.transcript || []);
        if (data.status === 'completed') {
          navigate(`/practice/results/${sessionId}`, { replace: true });
          return;
        }
        // Start the call - play AI greeting
        startCall(data);
      } else {
        toast.error('Session not found');
        navigate('/practice');
      }
    } catch (e) {
      toast.error('Failed to load session');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const startCall = async (sessionData) => {
    setCallState('ai_speaking');

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionData.session_id}/voice-start`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        if (data.audio_base64) {
          await playAudio(data.audio_base64);
        }
        setCallState('active');
      } else {
        setCallState('active');
      }
    } catch (e) {
      console.error('Voice start error:', e);
      setCallState('active');
    }
  };

  const playAudio = (base64Audio) => {
    return new Promise((resolve) => {
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioPlayerRef.current = audio;
      audio.onended = () => {
        setCallState('active');
        resolve();
      };
      audio.onerror = () => {
        setCallState('active');
        resolve();
      };
      setCallState('ai_speaking');
      audio.play().catch(() => {
        setCallState('active');
        resolve();
      });
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        processVoice();
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setCallState('listening');
      setRecordingDuration(0);

      // Recording timer + auto-interrupt at 30s
      recTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          if (prev >= 30) {
            stopRecording();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (e) {
      console.error('Mic error:', e);
      toast.error('Microphone access denied. Switching to text mode.');
      setFallbackToChat(true);
    }
  };

  const stopRecording = useCallback(() => {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
    setIsRecording(false);
    setRecordingDuration(0);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const processVoice = async () => {
    if (audioChunksRef.current.length === 0) {
      setCallState('active');
      return;
    }

    setCallState('processing');

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice.webm');

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/voice-message`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();

        if (data.error) {
          toast.error(data.error);
          setCallState('active');
          return;
        }

        // Add rep message to transcript
        if (data.rep_text) {
          setTranscript(prev => [...prev, {
            speaker: 'rep',
            timestamp: new Date().toISOString(),
            message: data.rep_text
          }]);
        }

        // Add buyer message to transcript
        if (data.buyer_text) {
          setTranscript(prev => [...prev, {
            speaker: 'buyer',
            timestamp: new Date().toISOString(),
            message: data.buyer_text
          }]);
        }

        // Play AI audio response
        if (data.audio_base64) {
          await playAudio(data.audio_base64);
        } else {
          setCallState('active');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to process voice');
        setCallState('active');
      }
    } catch (e) {
      console.error('Voice processing error:', e);
      toast.error('Connection error. Switching to text mode.');
      setFallbackToChat(true);
      setCallState('active');
    }
  };

  const sendTextMessage = async () => {
    const text = chatInput.trim();
    if (!text) return;

    setChatInput('');
    setCallState('processing');

    setTranscript(prev => [...prev, {
      speaker: 'rep',
      timestamp: new Date().toISOString(),
      message: text
    }]);

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text })
      });

      if (res.ok) {
        const data = await res.json();
        setTranscript(prev => [...prev, data.message]);

        // Try to play as voice
        try {
          const voiceRes = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/voice-start`, {
            method: 'POST',
            credentials: 'include'
          });
          // We can't easily TTS just the response via this endpoint, so just set active
        } catch (e) {
          console.debug('Voice start failed (non-blocking):', e);
        }
        setCallState('active');
      } else {
        toast.error('Failed to get response');
        setCallState('active');
      }
    } catch (e) {
      toast.error('Connection error');
      setCallState('active');
    }
  };

  const endCall = async () => {
    if (callState === 'ending') return;
    setCallState('ending');

    cleanup();

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/end`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        navigate(`/practice/results/${sessionId}`);
      } else {
        toast.error('Failed to end session');
        setCallState('active');
      }
    } catch (e) {
      toast.error('Failed to end session');
      setCallState('active');
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!session) return null;

  const persona = session.persona || {};
  const BuyerIcon = PERSONA_ICONS[session.persona_id] || UserCircle;
  const lastBuyerMsg = [...transcript].reverse().find(m => m.speaker === 'buyer');
  const lastRepMsg = [...transcript].reverse().find(m => m.speaker === 'rep');

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="voice-roleplay">
        {/* Call Screen */}
        <div className="flex-1 flex flex-col items-center justify-center relative overflow-hidden">
          {/* Background pulse effect */}
          {callState === 'ai_speaking' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-64 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
          )}
          {callState === 'listening' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-64 rounded-full bg-emerald-500/5 animate-ping" style={{ animationDuration: '1.5s' }} />
            </div>
          )}

          {/* Buyer Avatar */}
          <div className={`relative mb-6 ${callState === 'ai_speaking' ? 'animate-pulse' : ''}`}>
            <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
              callState === 'ai_speaking' ? 'bg-primary/20 border-primary shadow-lg shadow-primary/20' :
              callState === 'listening' ? 'bg-secondary border-emerald-500/50' :
              callState === 'processing' ? 'bg-secondary border-yellow-500/50' :
              'bg-secondary border-border/50'
            }`}>
              <BuyerIcon className={`w-12 h-12 ${
                callState === 'ai_speaking' ? 'text-primary' : 'text-muted-foreground'
              }`} />
            </div>
            {callState === 'ai_speaking' && (
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                <div className="flex gap-0.5 items-end h-4">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="w-1 bg-primary rounded-full animate-bounce" style={{
                      height: `${8 + Math.random() * 12}px`,
                      animationDelay: `${i * 80}ms`,
                      animationDuration: '0.6s'
                    }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Persona info */}
          <h2 className="font-heading text-xl font-bold text-foreground">{persona.name || 'AI Buyer'}</h2>
          <p className="text-sm text-muted-foreground mb-1">{persona.job_title} — {persona.company_type}</p>

          <div className="flex items-center gap-3 mb-6">
            <Badge className={`border text-xs ${
              session.difficulty === 'hard' ? 'bg-red-500/15 text-red-600 border-red-500/30' :
              session.difficulty === 'easy' ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' :
              'bg-yellow-500/15 text-amber-600 border-yellow-500/30'
            }`}>
              {session.difficulty}
            </Badge>
            <div className="flex items-center gap-1.5 font-mono text-sm text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              {formatTime(elapsed)}
            </div>
          </div>

          {/* Status indicator */}
          <div className="mb-8 h-6 flex items-center gap-2">
            {callState === 'connecting' && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin" /> Connecting...
              </span>
            )}
            {callState === 'ai_speaking' && (
              <span className="flex items-center gap-2 text-sm text-primary">
                <Volume2 className="w-4 h-4" /> Buyer is speaking...
              </span>
            )}
            {callState === 'listening' && (
              <span className="flex items-center gap-2 text-sm text-emerald-600">
                <Radio className="w-4 h-4 animate-pulse" /> Listening... ({recordingDuration}s)
              </span>
            )}
            {callState === 'processing' && (
              <span className="flex items-center gap-2 text-sm text-amber-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Processing...
              </span>
            )}
            {callState === 'active' && (
              <span className="text-sm text-muted-foreground">Tap mic to speak</span>
            )}
            {callState === 'ending' && (
              <span className="flex items-center gap-2 text-sm text-red-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing performance...
              </span>
            )}
          </div>

          {/* Live transcript bubble */}
          <div className="w-full max-w-lg px-4 space-y-2 mb-8">
            {lastBuyerMsg && (
              <div className="p-3 rounded-xl bg-secondary/40 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <BuyerIcon className="w-3 h-3" /> {persona.name}
                </p>
                <p className="text-sm text-foreground">{lastBuyerMsg.message}</p>
              </div>
            )}
            {lastRepMsg && (
              <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                <p className="text-xs text-primary/60 mb-1 flex items-center gap-1">
                  <UserCircle className="w-3 h-3" /> You
                </p>
                <p className="text-sm text-foreground">{lastRepMsg.message}</p>
              </div>
            )}
          </div>

          {/* Fallback text input */}
          {fallbackToChat && (
            <div className="w-full max-w-lg px-4 mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendTextMessage()}
                  placeholder="Type your response (voice unavailable)..."
                  disabled={callState === 'processing' || callState === 'ending'}
                  data-testid="voice-fallback-input"
                  className="flex-1 h-11 px-4 rounded-xl bg-background border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                />
                <Button
                  onClick={sendTextMessage}
                  disabled={!chatInput.trim() || callState === 'processing'}
                  className="h-11 w-11 rounded-xl p-0"
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Call Controls */}
        <div className="p-6 border-t border-border bg-card/80 backdrop-blur-sm">
          <div className="flex items-center justify-center gap-6">
            {/* Fallback toggle */}
            <Button
              variant="outline"
              size="icon"
              className="w-12 h-12 rounded-full border-border/50"
              onClick={() => setFallbackToChat(!fallbackToChat)}
              data-testid="toggle-text-mode"
              title={fallbackToChat ? 'Switch to voice' : 'Switch to text'}
            >
              <MessageSquare className={`w-5 h-5 ${fallbackToChat ? 'text-primary' : 'text-muted-foreground'}`} />
            </Button>

            {/* Mic Button */}
            {!fallbackToChat && (
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={callState === 'processing' || callState === 'ai_speaking' || callState === 'ending' || callState === 'connecting'}
                data-testid="mic-btn"
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-40 ${
                  isRecording
                    ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30 scale-110'
                    : 'bg-secondary hover:bg-secondary/80 border-2 border-border hover:border-primary/50'
                }`}
              >
                {isRecording ? (
                  <Mic className="w-8 h-8 text-white" />
                ) : (
                  <MicOff className="w-8 h-8 text-muted-foreground" />
                )}
              </button>
            )}

            {/* End Call */}
            <Button
              variant="destructive"
              size="icon"
              className="w-12 h-12 rounded-full"
              onClick={endCall}
              disabled={callState === 'ending'}
              data-testid="end-call-btn"
            >
              {callState === 'ending' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <PhoneOff className="w-5 h-5" />
              )}
            </Button>
          </div>

          {isRecording && !fallbackToChat && (
            <p className="text-center text-xs text-emerald-600 mt-3 animate-pulse">
              Recording... Tap mic to send ({30 - recordingDuration}s remaining)
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
